import React, { useState } from 'react';
import { X } from 'lucide-react';
import { THEME } from '../theme';
import { endpoints } from '../api/api';
import { useAuthStore } from '../store/authStore';

const AuthForm = ({ onClose, onSuccess }) => {
  const [isSignup, setIsSignup] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Get backend API URL from environment or default
  const API_BASE_URL = (process.env.REACT_APP_API_URL || "").replace("http://", "https://");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const response = isSignup 
        ? await endpoints.auth.signup({ username, email, password })
        : await endpoints.auth.signin({ email, password });

      const data = response.data;

      // Store token in localStorage
      // If server did not issue a token (e.g., signup pending access), show message
      if (!data.token) {
        const message = data.msg || 'Account created. Awaiting access approval.';
        setError(message);
        setLoading(false);
        return;
      }

      // Use authStore for token and user management
      const authStore = useAuthStore.getState();
      authStore.setUser(data.user, data.token);

      // DIFFERENT HANDLING FOR SIGNUP VS SIGNIN
      if (isSignup) {
        // After successful signup: switch to login mode with success message
        setSuccessMessage('Account created successfully! Please login.');
        setIsSignup(false);
        setPassword(''); // Clear password for security
        setUsername('');
        setLoading(false);
        // Keep email filled for convenience
      } else {
        // After successful signin: redirect to dashboard
        if (onSuccess) {
          onSuccess(data);
        }
        // Force redirect to dashboard
        window.location.href = '/dashboard';
      }
    } catch (err) {
      console.error('Auth error:', err);
      // Extract error message from axios error
      const errorMsg = err.response?.data?.msg || 
                       err.message || 
                       'Authentication failed. Please try again.';
      setError(errorMsg);
      setLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    // Redirect to Google OAuth endpoint
    window.location.href = `${API_BASE_URL}/auth/google`;
  };

  const toggleMode = () => {
    setIsSignup(!isSignup);
    setError('');
    setSuccessMessage('');
    setEmail('');
    setPassword('');
    setUsername('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300 p-4">
      <div className="relative bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-md p-6 md:p-8 border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-300 overflow-hidden max-h-[90vh] overflow-y-auto">
        
        {/* Decorative Top Gradient */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-teal-500 to-cyan-500" />

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="text-center mb-6 md:mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-teal-50 dark:bg-slate-800 mb-4">
                <img src="/hicapy.png" alt="Logo" className="w-8 h-8 object-contain" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            {isSignup ? 'Join HiCapy' : 'Welcome Back'}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
            {isSignup ? 'Start your autonomous journey today' : 'Login to manage your assistant'}
            </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Username field (only for signup) */}
          {isSignup && (
            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5 ml-1">Username</label>
              <input
                type="text"
                id="username"
                placeholder="johndoe"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required={isSignup}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all text-sm font-medium"
              />
            </div>
          )}

          {/* Email field */}
          <div>
            <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5 ml-1">Email Address</label>
            <input
              type="email"
              id="email"
              placeholder="name@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all text-sm font-medium"
            />
          </div>

          {/* Password field */}
          <div>
            <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5 ml-1">Password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all text-sm font-medium"
            />
          </div>

          {/* Show Password Checkbox */}
          <div className="flex items-center px-1">
            <input
              type="checkbox"
              id="showPassword"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
              className="mr-2 w-4 h-4 text-teal-600 border-slate-300 rounded focus:ring-teal-500"
            />
            <label htmlFor="showPassword" className="text-sm text-slate-600 dark:text-slate-400 cursor-pointer select-none font-medium">
              Show Password
            </label>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="text-green-600 dark:text-green-400 text-sm text-center bg-green-50 dark:bg-green-900/20 p-3 rounded-xl border border-green-100 dark:border-green-900/30 font-medium">
              {successMessage}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="text-red-600 dark:text-red-400 text-sm text-center bg-red-50 dark:bg-red-900/20 p-3 rounded-xl border border-red-100 dark:border-red-900/30 font-medium">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3.5 rounded-xl font-bold shadow-lg shadow-teal-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
          >
            {loading ? 'Processing...' : isSignup ? 'Create Account' : 'Sign In'}
          </button>
        </form>

        <div className="relative my-8">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200 dark:border-slate-800"></div></div>
            <div className="relative flex justify-center text-xs font-bold uppercase tracking-wider"><span className="px-3 bg-white dark:bg-slate-900 text-slate-400">Or continue with</span></div>
        </div>

        {/* Google Auth Button */}
        <button
          onClick={handleGoogleAuth}
          className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-white py-3 rounded-xl font-bold transition-colors flex items-center justify-center gap-3"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          Google
        </button>

        {/* Toggle Sign Up/Sign In */}
        <div className="mt-8 text-center">
          <button
            onClick={toggleMode}
            className="text-sm text-slate-600 dark:text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors font-bold"
          >
            {isSignup ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthForm;

